import {Component, Input} from '@angular/core';
import {MembershipPlanResponse, MembershipResponse, CompanyResponse} from '../../../../../../@shared/models/response.module';
import {MembershipService} from '../../../../../../@shared/services/membership-service';
import {PaymentService} from '../../../../../../@shared/services/payment.service';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {AQH_MEM_0, AQH_MEM_1, AQH_MEM_4} from '../../../../../../@shared/models/input.module';
import {StorageService} from '../../../../../../@core/services/storage.service';
import {EndpointService} from '../../../../../../@shared/endpoints.service';
import {forkJoin, of} from 'rxjs';

export interface CompanyPlanGroup {
  company: CompanyResponse | null;
  companyId: string;
  companyName: string;
  companyLogo: string | null;
  plans: MembershipPlanResponse[];
  minPrice: number;
  maxPrice: number;
  planCount: number;
  hasIndividual: boolean;
  hasFamily: boolean;
  isExpanded: boolean;
}

@Component({
  selector: 'app-purchase-membership',
  standalone: false,
  templateUrl: './purchase-membership.html',
  styleUrl: './purchase-membership.css'
})
export class PurchaseMembership {
  // Optional: when set, only show plans for this company
  @Input() companyId: string | null = null;
  @Input() company: CompanyResponse | null = null;
  // Optional: when set, we're renewing a specific plan
  @Input() renewPlan: MembershipPlanResponse | null = null;

  plans: MembershipPlanResponse[] = [];
  availablePlans: MembershipPlanResponse[] = [];
  companyGroups: CompanyPlanGroup[] = [];
  filteredGroups: CompanyPlanGroup[] = [];
  isRenewal: boolean = false;
  userMemberships: MembershipResponse[] = [];
  selectedPlan: MembershipPlanResponse | null = null;
  loading: boolean = false;
  purchasing: boolean = false;
  searchQuery: string = '';

  constructor(private membershipService: MembershipService,
              private paymentService: PaymentService,
              private storageService: StorageService,
              private endpointService: EndpointService,
              private modal: NgbActiveModal) {
  }

  ngOnInit() {
    // Check if we're in renewal mode
    if (this.renewPlan) {
      this.isRenewal = true;
      this.availablePlans = [this.renewPlan];
      this.selectedPlan = this.renewPlan;
      this.loading = false;
    } else {
      this.loadPlansAndMemberships();
    }
  }

  loadPlansAndMemberships() {
    this.loading = true;

    // If companyId is provided, load plans for that company only
    const plansParams: AQH_MEM_4 = this.companyId ? { company_id: this.companyId } : {};
    const membershipsParams: AQH_MEM_0 = { active_only: true };

    // Use public endpoint if companyId is provided (for organisation details page)
    const plansObservable = this.companyId
      ? this.endpointService.aqh_mem_11(this.companyId, false)
      : this.membershipService.getMembershipPlans(plansParams);

    forkJoin({
      plans: plansObservable,
      memberships: this.membershipService.getUserMembership(membershipsParams)
    }).subscribe({
      next: (result: any) => {
        // Handle both response formats (direct array or {data: array})
        this.plans = result.plans?.data || result.plans || [];
        this.userMemberships = result.memberships || [];
        this.filterAvailablePlans();
        this.groupPlansByCompany();
        this.applySearch();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  filterAvailablePlans() {
    // Get company IDs where user already has an active membership
    const activeCompanyIds = new Set(
      this.userMemberships
        .filter(m => m.status === 'active' && m.plan?.company_id)
        .map(m => m.plan!.company_id)
    );

    // Filter out plans from companies where user already has active membership
    this.availablePlans = this.plans.filter(plan =>
      !activeCompanyIds.has(plan.company_id)
    );
  }

  groupPlansByCompany() {
    const groupMap = new Map<string, CompanyPlanGroup>();

    for (const plan of this.availablePlans) {
      const companyId = plan.company_id;

      if (!groupMap.has(companyId)) {
        groupMap.set(companyId, {
          company: plan.company,
          companyId: companyId,
          companyName: plan.company?.company_name || 'Unknown Organisation',
          companyLogo: plan.company?.company_logo_url || null,
          plans: [],
          minPrice: Infinity,
          maxPrice: 0,
          planCount: 0,
          hasIndividual: false,
          hasFamily: false,
          isExpanded: false
        });
      }

      const group = groupMap.get(companyId)!;
      group.plans.push(plan);
      group.planCount++;
      group.minPrice = Math.min(group.minPrice, plan.price_cents);
      group.maxPrice = Math.max(group.maxPrice, plan.price_cents);

      if (plan.coverage === 'individual') {
        group.hasIndividual = true;
      } else if (plan.coverage === 'family') {
        group.hasFamily = true;
      }
    }

    // Convert map to array and sort by company name
    this.companyGroups = Array.from(groupMap.values())
      .sort((a, b) => a.companyName.localeCompare(b.companyName));

    // If only one company, expand it by default
    if (this.companyGroups.length === 1) {
      this.companyGroups[0].isExpanded = true;
    }
  }

  applySearch() {
    const query = this.searchQuery.toLowerCase().trim();

    if (!query) {
      this.filteredGroups = [...this.companyGroups];
      return;
    }

    this.filteredGroups = this.companyGroups.filter(group => {
      // Search in company name
      if (group.companyName.toLowerCase().includes(query)) {
        return true;
      }
      // Search in plan names
      return group.plans.some(plan =>
        plan.name.toLowerCase().includes(query) ||
        plan.code.toLowerCase().includes(query) ||
        (plan.description && plan.description.toLowerCase().includes(query))
      );
    });
  }

  onSearchChange() {
    this.applySearch();
  }

  toggleGroup(group: CompanyPlanGroup) {
    group.isExpanded = !group.isExpanded;
  }

  selectPlan(plan: MembershipPlanResponse) {
    this.selectedPlan = plan;
  }

  purchaseMembership() {
    if (!this.selectedPlan) return;

    this.purchasing = true;

    // Check if company has Stripe payments enabled and plan has a price
    if (this.isPaymentRequired()) {
      // Redirect to Stripe checkout
      const baseUrl = window.location.origin;
      const successUrl = `${baseUrl}/user-profile`;
      const cancelUrl = `${baseUrl}/user-profile`;

      this.paymentService.createMembershipCheckout(this.selectedPlan.plan_id, successUrl, cancelUrl).subscribe({
        next: (response) => {
          // Redirect to Stripe checkout
          window.location.href = response.checkout_url;
        },
        error: () => {
          this.purchasing = false;
        }
      });
    } else {
      // Free membership - create directly
      const params: AQH_MEM_1 = {
        plan_id: this.selectedPlan.plan_id
      };

      this.membershipService.postMembership(params).subscribe({
        next: () => {
          this.purchasing = false;
          this.modal.close(true);
        },
        error: () => {
          this.purchasing = false;
        }
      });
    }
  }

  isPaymentRequired(): boolean {
    if (!this.selectedPlan) return false;
    // Payment is required if the plan has a price and the company has Stripe enabled
    return this.selectedPlan.price_cents > 0 && this.isCompanyStripeEnabled();
  }

  isCompanyStripeEnabled(): boolean {
    return this.selectedPlan?.company?.stripe_charges_enabled === true;
  }

  close() {
    this.modal.close();
  }

  formatPrice(cents: number): string {
    return (cents / 100).toFixed(0);
  }

  getPriceRange(group: CompanyPlanGroup): string {
    if (group.minPrice === group.maxPrice) {
      return `${this.formatPrice(group.minPrice)} kr`;
    }
    return `${this.formatPrice(group.minPrice)} - ${this.formatPrice(group.maxPrice)} kr`;
  }

  getPlanTypes(group: CompanyPlanGroup): string[] {
    const types: string[] = [];
    if (group.hasIndividual) types.push('individual');
    if (group.hasFamily) types.push('family');
    return types;
  }
}
